import axios from "axios";

type txFormData = {
    tokenMint: string;
    amount: number;
    slippage: number;
    publicKey: string;
    buyType: number;
    priorityFeeInLamports: number | null;
}
export const prepareLaunchTransaction = async (formData: FormData) => {
    try {
        const response = await axios.post(
            `${process.env.NEXT_PUBLIC_API_URL}/transactions/prepare-launch-transaction`,
            formData,
            {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            }
        );
        return response.data;
    } catch (error) {
        console.error('Error preparing launch transaction:', error);
        throw error;
    }
}

export const prepareBuyTransaction = async (formData: txFormData) => {
    try {
        const response = await axios.post(`${process.env.NEXT_PUBLIC_API_URL}/transactions/prepare-buy-transaction`, formData);
        return response.data;
    } catch (error) {
        console.error('Error preparing buy transaction:', error);
        throw error;
    }
}
export const prepareSellTransaction = async (formData: txFormData) => {
    try {
        const response = await axios.post(`${process.env.NEXT_PUBLIC_API_URL}/transactions/prepare-sell-transaction`, formData);
        return response.data;
    } catch (error) {
        console.error('Error preparing sell transaction:', error);
        throw error;
    }
}

export const verifyTransaction = async (transactionSignature: string) => {
    try {
        const response = await axios.get(`${process.env.NEXT_PUBLIC_API_URL}/transactions/verify/${transactionSignature}`);
        return response.data;
    } catch (error) {
        console.error('Error verifying transaction:', error);
        throw error;
    }
}


// Helper functions
export function formatCurrency(num: number): string {
    if (!num) return '$0.00'; // Handle null, undefined, or 0
    if (num < 0.0001 && num > 0) {
        // Use exponential notation for very small numbers to avoid excessive zeros
        return `$${num.toExponential(2)}`;
    }
    if (num >= 1_000_000_000) {
        return '$' + (num / 1_000_000_000).toFixed(2) + 'B';
    }
    if (num >= 1_000_000) {
        return '$' + (num / 1_000_000).toFixed(2) + 'M';
    }
    if (num >= 1_000) {
        return '$' + (num / 1_000).toFixed(2) + 'K';
    }
    // Format small numbers with appropriate precision, default to 2 decimal places for >= 1
    return `$${num.toFixed(num < 1 ? 4 : 2)}`;
}

export function formatDateTime(dateString: string): string {
    if (!dateString) return 'N/A'; // Handle null or undefined date strings
    try {
        const date = new Date(dateString);
        // Check if the date is valid
        if (isNaN(date.getTime())) {
            return 'Invalid Date';
        }
        return new Intl.DateTimeFormat('en-US', {
            month: 'long',
            day: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
            hour12: true,
        }).format(date).replace(',', ' at');
    } catch (error) {
        console.error("Error formatting date:", error);
        return 'Error'; // Return an error message or handle appropriately
    }
}

export function formatNumber(num: number | undefined | null): string {
    if (num === null || num === undefined || isNaN(num)) {
        return 'N/A'; // Handle null, undefined, or NaN inputs
    }

    if (num === 0) {
        return '0'; // Handle zero explicitly
    }

    const absNum = Math.abs(num); // Work with the absolute value for magnitude checks
    if (absNum >= 1_000_000_000) {
        return (num / 1_000_000_000).toFixed(2).replace(/\.00$/, '') + 'B';
    } if (absNum >= 1_000_000) {
        return (num / 1_000_000).toFixed(2).replace(/\.00$/, '') + 'M';
    }
    if (absNum >= 1_000) {
        return (num / 1_000).toFixed(2).replace(/\.00$/, '') + 'K';
    }
    // For numbers less than 1000, return the number itself, potentially with decimals
    // Adjust precision as needed, for example, for very small numbers
    if (absNum < 1000 && absNum > 0) {
        // Use more precision for small decimals if needed, or format as is
        // Example: return num.toFixed(4); if more precision is desired
        return num.toFixed(2); // Or adjust formatting as required
    }
    // Return the number rounded to an integer if it's between 1 and 999 - This case is now covered above
    // return Math.round(num).toString(); 
    // Fallback for any unexpected cases (like negative numbers not handled by absNum checks, though unlikely here)
    return num.toFixed(2);
}